<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link to="/app/notes/list" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">Add Note</h6>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-lg-8">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Title <span class="text-danger">*</span></label>
                  <input v-model="form.title" type="text" class="form-control" required />
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Course <span class="text-danger">*</span></label>
                  <select v-model="form.courseId" class="form-select" required>
                    <option value="">Select Course</option>
                    <option v-for="course in courses" :key="course.id" :value="course.id">{{ course.name }}</option>
                  </select>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Type <span class="text-danger">*</span></label>
                  <select v-model="form.type" class="form-select" required>
                    <option value="">Select Type</option>
                    <option value="pdf">PDF</option>
                    <option value="text">Text</option>
                    <option value="video">Video Link</option>
                  </select>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Status</label>
                  <select v-model="form.status" class="form-select">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>

              <div v-if="form.type === 'text'" class="mb-3">
                <label class="form-label">Content <span class="text-danger">*</span></label>
                <textarea v-model="form.content" class="form-control" rows="10" required></textarea>
              </div>

              <div v-if="form.type === 'pdf'" class="mb-3">
                <label class="form-label">Upload PDF <span class="text-danger">*</span></label>
                <input type="file" class="form-control" @change="handleFile" accept=".pdf" required />
              </div>

              <div v-if="form.type === 'video'" class="mb-3">
                <label class="form-label">Video URL <span class="text-danger">*</span></label>
                <input v-model="form.videoUrl" type="url" class="form-control" placeholder="https://..." required />
              </div>

              <div class="mb-3">
                <label class="form-label">Description</label>
                <textarea v-model="form.description" class="form-control" rows="2"></textarea>
              </div>

              <div v-if="error" class="alert alert-danger">{{ error }}</div>

              <div class="d-flex gap-2 pt-2">
                <button type="submit" class="btn btn-primary" :disabled="loading">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  Create Note
                </button>
                <router-link to="/app/notes/list" class="btn btn-outline-secondary">Cancel</router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'AddNote',
  setup() {
    const router = useRouter()
    const alert = useAlert()
    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Notes', url: '/app/notes/list' },
      { title: 'Add Note', active: true }
    ]

    const form = ref({ title: '', courseId: '', type: '', status: 'draft', content: '', videoUrl: '', description: '' })
    const courses = ref([])
    const file = ref(null)
    const loading = ref(false)
    const error = ref('')

    const fetchCourses = async () => {
      try {
        const response = await api.get('/v1/admin/courses')
        courses.value = response.data.courses
      } catch (err) {
        console.error('Failed to fetch courses:', err)
      }
    }

    const handleFile = (e) => { file.value = e.target.files[0] }

    const handleSubmit = async () => {
      loading.value = true
      error.value = ''

      const formData = new FormData()
      Object.keys(form.value).forEach(key => formData.append(key, form.value[key]))
      if (file.value) formData.append('file', file.value)

      try {
        await api.post('/v1/admin/notes', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        alert.success('Success', 'Note created successfully')
        router.push('/app/notes/list')
      } catch (err) {
        error.value = err.response?.data?.message || 'Failed to create note'
      } finally {
        loading.value = false
      }
    }

    onMounted(fetchCourses)

    return { breadcrumbs, form, courses, loading, error, handleFile, handleSubmit }
  }
}
</script>
